/**
 * A short two-note chime, synthesised rather than shipped.
 *
 * An audio file would need a CSP-safe data URI in the bundle for one sound
 * that plays a few times a week; two oscillators cost nothing and can't 404.
 * The sound exists for one reason: an ownership request has a ten-minute fuse,
 * and a bell badge on another tab is not something anyone notices in time.
 */

const MUTE_KEY = 'govcrm:chimeMuted'

// One context for the page. Creating one per sound leaks them — browsers cap
// how many a document may hold, and the cap is low enough to hit.
let ctx: AudioContext | null = null

export function chimeMuted(): boolean {
  try {
    return localStorage.getItem(MUTE_KEY) === '1'
  } catch {
    // Private windows can throw on access; a missing preference is "unmuted".
    return false
  }
}

export function setChimeMuted(muted: boolean) {
  try {
    localStorage.setItem(MUTE_KEY, muted ? '1' : '0')
  } catch {
    // Preference doesn't persist; the toggle still works for this session.
  }
}

/**
 * Browsers refuse to start audio until the page has been interacted with.
 * Calling this from any early click gets the context into a running state so
 * the first real chime isn't the one that gets swallowed.
 */
export function primeChime() {
  try {
    if (!ctx) ctx = new AudioContext()
    if (ctx.state === 'suspended') void ctx.resume()
  } catch {
    // No Web Audio (or blocked): the UI is still fully usable in silence.
  }
}

/** Two rising notes, ~0.45s total. No-ops when muted or when audio is blocked. */
export function playChime() {
  if (chimeMuted()) return
  try {
    if (!ctx) ctx = new AudioContext()
    if (ctx.state === 'suspended') void ctx.resume()
    const now = ctx.currentTime
    // A fifth apart, sine waves — an alert that carries across a room without
    // sounding like an error.
    for (const [i, freq] of [660, 990].entries()) {
      const osc = ctx.createOscillator()
      const gain = ctx.createGain()
      osc.type = 'sine'
      osc.frequency.value = freq
      const start = now + i * 0.18
      // Ramps rather than steps: an abrupt gain change clicks.
      gain.gain.setValueAtTime(0.0001, start)
      gain.gain.exponentialRampToValueAtTime(0.18, start + 0.02)
      gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.26)
      osc.connect(gain).connect(ctx.destination)
      osc.start(start)
      osc.stop(start + 0.3)
    }
  } catch {
    // Autoplay policy or no Web Audio — the visual prompt still appears.
  }
}
